import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ApiError, NetworkError } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { useT } from '@/i18n/LanguageContext';
import { LanguageToggle } from '@/i18n/LanguageToggle';
import { describeStaffError } from '@/pages/Users/staff';

/**
 * ADR-084. Staff sign in with email + password; the device keeps the sign-in
 * for up to 30 days. The service-key form (ADR-061 bearer token) stays as a
 * secondary option for the transition of an existing administrator and for
 * integrations - it is not how employees are expected to sign in.
 */
export function LoginPage(): JSX.Element {
  const { state, login } = useAuth();
  const t = useT();
  const [mode, setMode] = useState<'password' | 'key'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizations, setOrganizations] = useState<readonly { organizationId: string; name: string }[] | null>(null);
  const [organizationId, setOrganizationId] = useState('');
  const [token, setTokenValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [forgot, setForgot] = useState(false);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  async function onPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await endpoints.login({ email: email.trim(), password, ...(organizationId ? { organizationId } : {}) });
      if ('chooseOrganization' in r) {
        setOrganizations(r.chooseOrganization);
        setOrganizationId(r.chooseOrganization[0]?.organizationId ?? '');
        return;
      }
      setPassword('');
      await login(r.sessionToken);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onKey(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(token.trim());
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell card">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LanguageToggle />
      </div>
      <h1 style={{ fontSize: 18, marginTop: 0 }}>{t('login.title')}</h1>
      {mode === 'password' ? (
        <form onSubmit={(e) => void onPassword(e)} className="wh-form" aria-label={t('login.passwordFormLabel')}>
          <div className="field-row">
            <label htmlFor="login-email">{t('login.email')}</label>
            <input id="login-email" type="email" autoComplete="username" value={email} onChange={(e) => { setEmail(e.target.value); setOrganizations(null); setOrganizationId(''); }} required />
          </div>
          <div className="field-row">
            <label htmlFor="login-password">{t('login.password')}</label>
            <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {organizations && (
            <div className="field-row">
              <label htmlFor="login-org">{t('login.organization')}</label>
              <select id="login-org" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
                {organizations.map((o) => (
                  <option key={o.organizationId} value={o.organizationId}>{o.name}</option>
                ))}
              </select>
              <p className="field-hint">{t('login.organizationHint')}</p>
            </div>
          )}
          {Boolean(error) && (
            <div className="error-banner" role="alert">
              {describeLoginError(t, error)}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={submitting || !email.trim() || !password}>
              {submitting ? t('login.signingIn') : t('login.signIn')}
            </button>
          </div>
          <p className="field-hint">{t('login.staysSignedIn')}</p>
          <div className="login-links">
            <button type="button" className="link-button" onClick={() => setForgot((v) => !v)} aria-expanded={forgot}>
              {t('login.forgot')}
            </button>
            <button type="button" className="link-button" onClick={() => { setError(null); setMode('key'); }}>
              {t('login.useServiceKey')}
            </button>
          </div>
          {forgot && <p className="notice" role="note">{t('login.forgotHelp')}</p>}
        </form>
      ) : (
        <form onSubmit={(e) => void onKey(e)} aria-label={t('login.keyFormLabel')}>
          <p className="field-hint">{t('login.serviceKeyHint')}</p>
          <div className="field-row">
            <label htmlFor="token">{t('login.bearerToken')}</label>
            <input id="token" type="password" autoComplete="off" value={token} onChange={(e) => setTokenValue(e.target.value)} required />
          </div>
          {Boolean(error) && (
            <div className="error-banner" role="alert">
              {describeLoginError(t, error, true)}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={submitting || token.trim().length === 0}>
              {submitting ? t('login.signingIn') : t('login.signIn')}
            </button>
            <button type="button" onClick={() => { setError(null); setMode('password'); }}>
              {t('login.backToPassword')}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}

function describeLoginError(t: ReturnType<typeof useT>, error: unknown, key = false): string {
  if (error instanceof ApiError && error.status === 429) return t('staff.err.tooMany');
  if (key && error instanceof ApiError && (error.status === 401 || error.status === 403)) return t('login.tokenInvalid');
  const specific = describeStaffError(t, error);
  if (specific) return specific;
  if (error instanceof NetworkError) return error.message;
  return t('login.couldNotSignIn');
}
